import { Injectable } from '@angular/core';
import { Appointment, CalendarAvailability, CalendarSession } from '../models';
import { DatabaseService } from './database.service';
import { HelperService } from './helper.service';

@Injectable({
  providedIn: 'root'
})
export class CalendarService {

  constructor(
    private dbService: DatabaseService,
    private helperService: HelperService,
  ) { }

  public async getOrganizationSessionsAsPromise(orgId: string) {
    const sessions = await this.dbService.getSnapshot(`/organizationSessions/${orgId}`);
    return sessions? this.helperService.transformFirebaseObjectToArray(sessions): null;
  }

  public async  getOrganizationSessionAsPromise(orgId: string, sessionId: string): Promise<CalendarSession> {
    const appt = await this.dbService.getSnapshot(`/organizationSessions/${orgId}/${sessionId}`);
    return appt? new CalendarSession({
      id: sessionId,
      ...appt
    }) : null;
  }

  public addOrganizationSession(orgId: string, session: Partial<CalendarSession>) {
    return this.dbService.pushByRef(`organizationSessions/${orgId}`, session);
  }

  public updateOrganizationSession(orgId: string, sessionId: string, session: Partial<CalendarSession>): Promise<any> {
    return this.dbService.updateRef(`organizationSessions/${orgId}/${sessionId}`, session);
  }

  public deleteOrganizationSession(orgId: string, sessionId: string): Promise<any> {
    return this.dbService.removeRef(`organizationSessions/${orgId}/${sessionId}`);
  }

  public async  getUserAvailabilityAsPromise(userId: string, availabilityId: string): Promise<CalendarAvailability | null> {
    const appt = await this.dbService.getSnapshot(`/userAvailabilities/${userId}/${availabilityId}`);
    return appt? new CalendarAvailability({
      id: availabilityId,
      ...appt
    }) : null;
  }

  public async  getUserAvailabilitiesAsPromise(userId: string): Promise<CalendarAvailability[] | null> {
    const appt = await this.dbService.getSnapshot(`/userAvailabilities/${userId}`);
    return appt? this.helperService.transformFirebaseObjectToArray(appt).map(item => {return new CalendarAvailability(item)}) : null;
  }

  public getUserAvailabilitiesList(userId: string) {
    return this.dbService.getListByRefTest(`userAvailabilities/${userId}`);
  }

  public addUserAvailability(userId: string, availability: Partial<CalendarAvailability>) {
    return this.dbService.pushByRef(`userAvailabilities/${userId}`, availability);
  }

  public updateUserAvailability(userId: string, availabilityId: string, appointment: Partial<CalendarAvailability>): Promise<any> {
    return this.dbService.updateRef(`userAvailabilities/${userId}/${availabilityId}`, appointment);
  }

  public deleteUserAvailability(userId: string, availabilityId: string): Promise<any> {
    return this.dbService.removeRef(`userAvailabilities/${userId}/${availabilityId}`);
  }

  public addAvailabilityException(userId: string, availabilityId: string, occurrence: string){
    return this.dbService.pushByRef(`userAvailabilities/${userId}/${availabilityId}/recurring/exclusions`, {
      timestamp: occurrence
    });
  }

  public async addAvailabitySeriesAppointment(userId: string, availability: Partial<CalendarAvailability>, availabilityId: string, originalOccurrence: string) {
    const ref = await this.addUserAvailability(userId, {
      ...availability,
      parent: {
        id: availabilityId,
        occurrence: originalOccurrence
      }
    });

    await this.addAvailabilityException(userId, availabilityId, originalOccurrence);
    return ref
  }
}
